import type { Messages } from './en';

/**
 * Mundo de las fases B/C: casas que se pueden visitar, el mercado nocturno, festivales y excursiones.
 * Las frases marcadas como práctica de inglés se quedan en inglés a propósito.
 */
export const esWorld3: Pick<Messages, 'world3'> = {
  world3: {
    mapNames: { grandma: 'Casa de la abuela Lee', bo: 'Casa de Bo', market: 'Mercado nocturno de los Faroles' },
    zones: { grandma: 'Casa de la abuela Lee', bo: 'Casa de Bo', market: 'Mercado nocturno', marketGames: 'Puestos de juegos' },
    objects: {
      grTea: 'Mesa del té',
      grCooker: 'Olla arrocera',
      grPhotos: 'Fotos de familia',
      grSewing: 'Máquina de coser',
      grCalendar: 'Calendario de hojas',
      boConsole: 'Videoconsola',
      boAquarium: 'Acuario',
      boComics: 'Estantería de cómics',
      boLego: 'Bloques de construcción',
      mkTea: 'Puesto de té de burbujas',
      mkChicken: 'Puesto de pollo frito',
      mkBalls: 'Bolitas de boniato',
      mkTofu: 'Puesto de tofu apestoso',
      mkOyster: 'Puesto de tortilla de ostras',
      mkIce: 'Puesto de hielo raspado',
      mkFish: 'Pesca de pececitos',
      mkRings: 'Lanzamiento de aros',
      mkClaw: 'Máquinas de gancho',
      marketGate: 'Entrada del mercado nocturno',
      fsRiddles: 'Adivinanzas de los faroles',
      fsTangyuan: 'Carrito de tangyuan',
      fsBoat: 'Barcas dragón',
      fsZongzi: 'Mesa de zongzi',
      fsEgg: 'Mesa del huevo de pie',
      fsBBQ: 'Barbacoa del Festival de la Luna',
      fsPomelo: 'Cesta de pomelos',
      fsLion: 'Danza del león',
      fsReunion: 'Cena de Nochevieja',
    },
    npc: { boMom: 'Mamá de Bo', teaVendor: 'Tía Hua', chickenVendor: 'Tío Kang', gameVendor: 'Tío Lou', shopper1: 'Cliente', shopper2: 'Cliente', shopper3: 'Cliente' },

    closed: {
      grandma: 'No contesta nadie. La abuela Lee suele estar en casa por la tarde-noche, a partir de las 6.',
      bo: 'No hay nadie en casa. La familia de Bo vuelve a última hora de la tarde.',
      market: 'El mercado nocturno abre a las 5 de la tarde. ¡Vuelve esta noche!',
    },

    grandma: {
      hello: '¡Pasa, pasa! Quítate los zapatos y siéntate. ¿Te apetece un poco de té?',
      teaText: 'La abuela sirve té oolong en tacitas diminutas. «Primero huélelo y luego bebe a sorbitos.»',
      drink: '🍵 Tomar té con la abuela',
      teaDone: 'Calentito y aromático. La abuela te habla de las colinas de té donde creció.',
      teaWords: 'Práctica de inglés: “This tea smells lovely. Thank you, Grandma!”',
      cooker: 'La famosa olla arrocera roja. La abuela dice que es más vieja que tu mamá… ¡y que todavía hace los mejores bollos al vapor!',
      steam: '🥟 Hacer bollos al vapor juntos',
      steamDone: '¡Bollos de cerdo esponjosos! La abuela envuelve dos para tu familia.',
      photos: 'Fotos antiguas en blanco y negro: la abuela de niña en una granja, una boda, un bebé con una mantita roja…',
      readStory: '📖 Escuchar el cuento de la abuela',
      sewing: 'Una vieja máquina de coser con pedal. ¡Clac, clac, clac!',
      sewLearn: '🧵 Aprender a coser un botón',
      sewDone: '¡Has cosido tu primer botón! La abuela te regala una bufandita hecha a mano.',
      calendar: (date: string) => `Un calendario gordo de hojas para arrancar. La página de hoy: ${date}. ¡También dice qué días traen suerte!`,
      tearPage: '📅 Arrancar la página de hoy',
      tearDone: '¡Rasss! La abuela se ríe: «Es la mejor parte del día.»',
      noGrandma: 'La abuela Lee no está ahora mismo.',
      mochi: 'Mochi, la gatita, está hecha una bola en una cesta, ronroneando.',
      byeGift: 'Antes de que te vayas, la abuela te da una bolsa de naranjas. «¡Para tu familia!»',
    },
    bo: {
      momHello: '¡Anda, si es el amigo de Bo! Gracias por ayudarle con la cometa. ¿Quieres unos dumplings?',
      momDumplings: '🥟 ¡Sí, por favor! (en inglés: “Yes, please. They smell great!”)',
      momDone: 'Dumplings jugosos con un poquito de vinagre. La mamá de Bo sonríe: «¡Vuelve pronto!»',
      console: 'La videoconsola de Bo. «¡Juguemos juntos!»',
      playConsole: '🎮 Jugar una partida',
      aquarium: 'Tetras neón y una tortuguita perezosa. Bo la ha llamado «Cohete».',
      feedFish: '🐟 Dar de comer a los peces',
      fed: 'Los peces suben como flechas a por la comida. La tortuga Cohete no tiene ninguna prisa.',
      comics: 'Los cómics de Bo, en chino y en inglés. Algunas páginas tienen dibujitos en los márgenes.',
      readComic: '📖 Leer un cómic',
      lego: 'Una caja de bloques de construcción y una estación espacial a medio montar.',
      build: '🧱 Construir con Bo',
      built: 'Bo y tú termináis la estación espacial. «Tres, dos, uno… ¡despegue!»',
      boAway: 'Bo no está en casa ahora mismo.',
    },

    fest: {
      names: { lantern: 'Festival de los Faroles', dragonBoat: 'Festival de las Barcas Dragón', moon: 'Festival de la Luna (Medio Otoño)', newYear: 'Año Nuevo Lunar' },
      announce: (name: string) => `🎉 ¡Es la semana del ${name}! Ve al parque a celebrarlo.`,
      riddles: {
        intro: 'Hay farolillos rojos con adivinanzas colgados por todas partes. ¡Acierta y ganarás un premio! (Las adivinanzas están en inglés.)',
        guess: '🏮 Adivinar una adivinanza',
        right: '🎉 ¡Correcto! El guardián de los faroles te da una pegatina.',
        wrong: 'Esta vez no… ¡léela otra vez!',
        allDone: 'Has resuelto todas las adivinanzas de hoy. ¡Vuelve mañana!',
        list: [
          { q: 'I have hands but I can’t clap. What am I?', a: ['A clock', 'A tree', 'A shoe'] },
          { q: 'The more you take, the more you leave behind. What am I?', a: ['Footsteps', 'Cookies', 'Money'] },
          { q: 'I’m tall when I’m young and short when I’m old. What am I?', a: ['A candle', 'A dog', 'A book'] },
          { q: 'What has keys but can’t open any door?', a: ['A piano', 'A car', 'A teacher'] },
          { q: 'What goes up but never comes down?', a: ['Your age', 'A ball', 'Rain'] },
          { q: 'What has a neck but no head?', a: ['A bottle', 'A snake', 'A chair'] },
          { q: 'I’m full of holes, but I can still hold water. What am I?', a: ['A sponge', 'A cup', 'A net'] },
          { q: 'What gets wetter the more it dries?', a: ['A towel', 'The sun', 'A fish'] },
          { q: 'What can you catch but not throw?', a: ['A cold', 'A ball', 'A bus'] },
        ],
      },
      tangyuan: 'Tangyuan calentitos: bolitas dulces de arroz en sopa de jengibre. Son redondas como la luna llena y significan que la familia está unida.',
      eatTangyuan: '🥣 Comerse un cuenco',
      tangyuanDone: 'Blanditas y dulces, rellenas de sésamo negro. ¡Se te calienta la barriga!',
      boat: 'Dos barcas dragón largas se balancean en el estanque. El del tambor te saluda: «¿Te apuntas a la carrera?»',
      race: '🐉 Unirse a la carrera',
      zongzi: 'Hojas de bambú, arroz glutinoso, setas y cacahuetes. ¡Vamos a envolver zongzi! ¿Qué va primero?',
      zongziSteps: [
        { q: '¿Paso 1?', a: ['Doblar dos hojas de bambú en forma de cucurucho', 'Atar el cordel', 'Cocerlo al vapor'] },
        { q: '¿Paso 2?', a: ['Rellenarlo con arroz y relleno', 'Comérselo', 'Tirar las hojas'] },
        { q: '¿Paso 3?', a: ['Cerrar las hojas y atarlas con cordel', 'Meterlo en la nevera', 'Pintarlo'] },
      ],
      zongziWrong: 'Mmm, eso todavía no. ¡Piénsalo otra vez!',
      zongziDone: 'Tus zongzi van a la gran vaporera. Una hora después: ¡deliciosos!',
      egg: 'Una antigua tradición: dicen que justo a mediodía del Festival de las Barcas Dragón ¡puedes poner un huevo de pie!',
      tryEgg: '🥚 Intentar poner un huevo de pie',
      eggNoon: 'Todavía no es mediodía. ¡Vuelve entre las 11:00 y las 13:00!',
      eggWin: '¡Se sostiene! Todo el mundo aplaude. ¡Buena suerte para todo el año!',
      eggFail: 'Se tambalea… se tambalea… y se cae rodando. ¡Inténtalo otra vez!',
      bbq: 'Los vecinos asan mazorcas, salchichas y calamares en barbacoas pequeñitas mientras sale la luna.',
      joinBBQ: '🌽 Unirse a la barbacoa',
      bbqDone: 'Compartís pasteles de luna y maíz asado bajo la luna llena. «¡Feliz Festival de la Luna!»',
      pomelo: 'Una cesta de pomelos enormes. ¡Los niños se ponen la piel como un sombrero gracioso!',
      pomeloHat: '🍈 Hacer un sombrero de pomelo',
      pomeloDone: 'Llevas un sombrero de piel de pomelo. Todo el mundo se ríe… ¡y la fruta está jugosa y dulce!',
      lion: '¡Tambores y platillos! Un león dorado baila, salta y parpadea con sus ojazos.',
      watchLion: '🦁 Mirar y animar',
      lionDone: 'El león te hace una reverencia y sacude la melena. ¡Eso significa buena suerte!',
      reunion: 'Toda la familia alrededor de la mesa: pescado, dumplings, hot pot y fideos largos para una vida larga.',
      eatReunion: '🍲 Tomar la cena de reunión',
      reunionDone: 'Mamá y papá te dan un sobre rojo: ¡+88 monedas! Dilo en inglés: “Happy New Year! Thank you!”',
      reunionAgain: 'Ya has tomado la cena de reunión este Año Nuevo.',
    },

    stories: {
      title: '📖 La hora del cuento',
      pick: 'Elige un cuento. Toca 🔊 para oír cualquier frase, o deja que te lo lea entero.',
      readAll: '▶ Léemelo',
      stop: '⏹ Parar',
      gistBtn: '🌐 ¿De qué trata?',
      quiz: '❓ Preguntas',
      question: (n: number, total: number) => `Pregunta ${n} de ${total}`,
      right: '✅ ¡Correcto!',
      wrong: '❌ No exactamente… vuelve a mirar el cuento.',
      result: (score: number, total: number) => `¡Has acertado ${score} de ${total}!`,
      again: '🔁 Leer otra vez',
      list: '📚 Todos los cuentos',
      locked: '🔒 Encuentra este cuento en algún lugar del mundo',
      read: '✅ Leído',
      gists: {
        kite: 'La cometa de Bo se queda atascada en un árbol, y un amigo le ayuda a recuperarla.',
        mochi: 'La gatita de la abuela Lee se pierde persiguiendo una mariposa.',
        market: 'Lily es tímida, pero pide un té de burbujas en inglés en el mercado nocturno.',
        dragon: 'Cómo son las carreras de barcas dragón: ¡gana el trabajo en equipo!',
        grandma: 'La abuela Lee recuerda cuando crecía en una plantación de té.',
        comic: 'Un cómic divertido: una tortuguita construye un cohete.',
        tower: 'Curiosidades del Taipei 101: el ascensor superrápido y la bola dorada gigante.',
        gorge: 'La garganta de Taroko, en Hualien: acantilados de mármol y un río turquesa.',
      },
    },

    market: {
      welcome: '🏮 ¡Bienvenido al mercado nocturno! Pide la comida en inglés y te ganarás una sonrisa (y a veces algo de regalo).',
      look: 'Solo estoy mirando',
      orderPrompt: '¿Cómo lo pides? Elige la mejor frase en inglés.',
      wrong: [
        'El vendedor se ríe: «¡Ja! ¡Intenta decirlo con educación!»',
        '«¿Perdona? ¿Puedes repetirlo con más educación?»',
        'El vendedor levanta una ceja. ¡Prueba con una frase más amable!',
      ],
      cost: (n: number) => `${n} monedas`,
      poor: 'No tienes suficientes monedas.',
      extra: '«¡Qué bien hablas inglés! Toma, un poquito más de regalo.»',
      playFor: (n: number) => `▶ Jugar (${n} monedas)`,
      gamesText: '¡Luces, campanas y premios! Cada partida cuesta unas pocas monedas.',
      clawText: 'Una fila de máquinas de gancho iluminadas, llenas de peluches redondos y achuchables.',
      crowd: [
        '¡Cuántos olores! No sé qué comer primero.',
        'Lo que más me gusta son las bolitas de boniato. ¡Crujientes por fuera y blanditas por dentro!',
        '¿Has probado el tofu apestoso? Huele fuerte, pero está buenísimo.',
        '¡He ganado un peluche en la máquina de gancho! Bueno… después de doce intentos.',
        'No olvides guardarte la basura y tirarla en las papeleras.',
        'El tío de los pececitos dice que el truco es mover la redecilla despacio.',
      ],
      stalls: {
        mkTea: {
          sign: '珍珠奶茶',
          food: 'bubble tea',
          vendor: 'Hi there! What can I get you?',
          ok: 'Could I have a bubble tea with less sugar, please?',
          bad: ['Give me tea.', 'Bubble! Bubble! Now!'],
          done: 'Dulce, fresquito y lleno de perlas blanditas. ¡Sluuurp!',
          price: 30,
        },
        mkChicken: {
          sign: '炸雞排',
          food: 'fried chicken cutlet',
          vendor: 'Fresh chicken cutlet, bigger than your face! Spicy or not spicy?',
          ok: 'Not spicy, please. Can you cut it for me?',
          bad: ['Chicken. Fast.', 'I want the biggest one for free.'],
          done: 'Crujiente, jugoso y ENORME. ¡Necesitas las dos manos!',
          price: 40,
        },
        mkBalls: {
          sign: '地瓜球',
          food: 'sweet potato balls',
          vendor: 'Sweet potato balls, hot and crunchy! Small bag or big bag?',
          ok: 'A small bag, please. Thank you!',
          bad: ['Balls! Give!', 'I don’t like potatoes, give me a bag.'],
          done: 'Se hinchan como globitos dorados. ¡Crujientes y blanditas!',
          price: 20,
        },
        mkTofu: {
          sign: '臭豆腐',
          food: 'stinky tofu',
          vendor: 'Stinky tofu! It smells strong, but it’s delicious. Want to try?',
          ok: 'Sure, I’d like to try it, please!',
          bad: ['Ew, it stinks!', 'Make it not stink.'],
          done: 'Crujiente por fuera, tierno por dentro, con col encurtida. ¡Está sorprendentemente rico!',
          price: 35,
        },
        mkOyster: {
          sign: '蚵仔煎',
          food: 'oyster omelette',
          vendor: 'Oyster omelette with sweet sauce! For here or to go?',
          ok: 'For here, please. One oyster omelette.',
          bad: ['Omelette now!', 'I want it to go and to stay.'],
          done: 'Con mucho huevo, melosa y un poquito dulce. ¡Un clásico del mercado nocturno!',
          price: 45,
        },
        mkIce: {
          sign: '芒果冰',
          food: 'mango shaved ice',
          vendor: 'Mango shaved ice — mountains of mango! Would you like condensed milk on top?',
          ok: 'Yes, please! Could I also have an extra spoon for my friend?',
          bad: ['Ice. Mango. Go.', 'No milk, no mango, just give me ice.'],
          done: 'Hielo esponjoso, mango dulce y ¡un frío en el cerebro de lo más feliz!',
          price: 50,
        },
      },
    },
  },
};
